/**
 * Read every page of a kernel collection, not just the first.
 *
 * The kernel filters each page by permission (manifest \u2229 user) after it
 * reads it, so a page can be SHORT \u2014 or even EMPTY \u2014 and still not be the
 * last. The walk therefore ends only when the kernel stops handing back a
 * continuation (or says has_more: false), never because a page was short or
 * added nothing.
 *
 * The continuation is found under whichever name the answer uses. If it is a
 * URL, its query string is followed as-is. If it is a bare token, it is sent
 * as `cursor` first; if the kernel plainly ignored that (the same records came
 * back), the other common names are tried, and the one that works is kept.
 *
 * `complete` is false when the walk stopped for any reason other than reaching
 * the end, and `error` carries a later page's failure, so the screen can say
 * the list is partial instead of presenting it as everybody.
 */

const FIELDS = [
  "next_cursor", "nextCursor", "next_token", "nextToken", "next_page_token", "nextPageToken",
  "next_page", "next_key", "continuation", "continuation_token", "last_evaluated_key",
  "lastEvaluatedKey", "next", "cursor", "page_token", "after",
];
const NESTS = ["page", "pagination", "paging", "meta", "links"];

function asToken(v) {
  if (typeof v === "string" && v) return v;
  if (typeof v === "number") return String(v);
  if (v && typeof v === "object" && Object.keys(v).length) return JSON.stringify(v);
  return null;
}

function urlParams(token) {
  if (!/^(https?:\/\/|\/)/.test(token) && !/\?.*=/.test(token)) return null;
  try {
    const u = new URL(token, "https://kernel.invalid");
    const p = {};
    u.searchParams.forEach((v, k) => { p[k] = v; });
    return Object.keys(p).length ? p : null;
  } catch {
    return null;
  }
}

export function continuation(body) {
  if (!body || typeof body !== "object") return null;
  const scopes = [body, ...NESTS.map((n) => body[n]).filter((x) => x && typeof x === "object")];
  for (const s of scopes) {
    for (const f of FIELDS) {
      const token = asToken(s[f]);
      if (token) return { field: f, token, params: urlParams(token) };
    }
  }
  return null;
}

function namesFor(field, known) {
  const stripped = field.replace(/^next_?/i, "");
  const names = [known, "cursor", field, stripped, "next_token", "page_token", "next_cursor", "after", "exclusive_start_key"];
  return [...new Set(names.filter((n) => n && n.length > 1))];
}

function clean(p) {
  return Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined && v !== null));
}

export async function readAll(get, path, { limit = 50, maxPages = 200 } = {}) {
  const seen = new Set();
  const items = [];
  const take = (b) => {
    let fresh = 0;
    let total = 0;
    for (const it of b?.items || []) {
      total += 1;
      const k = it?.id ?? JSON.stringify(it);
      if (seen.has(k)) continue;
      seen.add(k);
      items.push(it);
      fresh += 1;
    }
    return { fresh, total };
  };

  let body;
  try {
    body = await get(path, clean({ limit }));
  } catch (e) {
    if (e?.isManifest || e?.status === 401 || e?.status === 403) throw e;
    body = await get(path, {});
    limit = undefined;
  }
  const first = body;
  let pages = 1;
  take(body);

  const sent = new Set();
  let paramName = null;
  let complete = false;
  let error = null;

  for (;;) {
    if (body?.has_more === false || body?.hasMore === false) { complete = true; break; }
    const next = continuation(body);
    if (!next || sent.has(next.token)) { complete = true; break; }
    if (pages >= maxPages) break;
    sent.add(next.token);

    const candidates = next.params
      ? [clean({ limit, ...next.params })]
      : namesFor(next.field, paramName).map((n) => clean({ limit, [n]: next.token }));

    let advanced = false;
    for (const p of candidates) {
      let b;
      try {
        b = await get(path, p);
      } catch (e) {
        error = e;
        continue;
      }
      pages += 1;
      const { fresh, total } = take(b);
      if (total > 0 && fresh === 0) continue;
      if (!next.params) paramName = Object.keys(p).find((k) => k !== "limit") || paramName;
      body = b;
      error = null;
      advanced = true;
      break;
    }
    if (!advanced) break;
  }
  return { ...(first || {}), items, pages, complete, error };
}
